import { useCallback, useContext, useMemo } from "react";
import { tpslRoiBasis } from "./tpslRoiBasis";
import {
  GetAccount200ResponsePositionsInner,
  InstrumentTypeResponse,
} from "../../codegen-api";
import { MarketInstrumentContext } from "../../contexts/MarketInstrumentContext";
import {
  getAssetFromSymbol,
  getContractPriceStep,
} from "../../utils/instruments";
import { TpslDesiredState } from "../../hooks/tpsl/tpsl.types";
import { calculateExitPriceFromRoi } from "../../utils/math";
import { ToastEnum, ToastStatusEnum } from "../../utils/toast";
import { TpslType } from "./TPSLForPositionModal.types";

/**
 * How long the backend caches the executor's open-trigger list before it will
 * enrich `positions[].triggers` from a fresh read (perpetual-backend
 * `TRIGGER_CACHE_TTL_MS`), plus a second of slack.
 */
export const TPSL_ENRICHMENT_CACHE_MS = 6_000;

interface UseTPSLDerivedParams {
  position?: GetAccount200ResponsePositionsInner;
  tpTriggerPrice: any;
  slTriggerPrice: any;
  tpRoi: any;
  slRoi: any;
  /**
   * The advanced slice of `TpslDesiredState` (size / limit prices / trigger
   * source) — already normalized by `useTPSLAdvanced`, so an unchecked
   * checkbox arrives here as `undefined` and the submit keeps its old shape.
   */
  desiredAdvanced?: Partial<TpslDesiredState>;
  reset: () => void;
  onHide?: () => void;
  setTpType: (t: TpslType) => void;
  setSlType: (t: TpslType) => void;
  defaultTpslType: TpslType;
  createTPSLOrders: any;
  mutate: any;
  playSound: any;
  addToast: any;
  t: any;
  apiError: any;
  setLoading: (b: boolean) => void;
  setErrorMessage: (s: string) => void;
}

export function useTPSLDerived({
  position,
  tpTriggerPrice,
  slTriggerPrice,
  tpRoi,
  slRoi,
  desiredAdvanced,
  reset,
  onHide,
  setTpType,
  setSlType,
  defaultTpslType,
  createTPSLOrders,
  mutate,
  playSound,
  addToast,
  t,
  apiError,
  setLoading,
  setErrorMessage,
}: UseTPSLDerivedParams) {
  const triggerPriceFromRoi = useCallback(
    (roi: number) => {
      if (!position) {
        return undefined;
      }
      // ROI is measured on the margin the position posts (as in useTPSLSync).
      // Unknown has no ROI-derived trigger: `Number(null)` silently switched
      // the basis to the entry price and produced a wrong price.
      const basis = tpslRoiBasis(position);
      if (basis === undefined) {
        return undefined;
      }
      return calculateExitPriceFromRoi(
        Number(position?.avg_entry_price),
        roi / 100,
        basis,
        position?.side!,
        Number(position?.amount)
      );
    },
    [position]
  );

  // After the bidirectional sync (effects below), tpTriggerPrice / slTriggerPrice
  // always hold the authoritative trigger price — whether the user typed Price
  // directly or Gain/Loss (the sync writes the derived price back into the price
  // field). So we can read the trigger price straight from those fields.
  const [actualTpTriggerPrice, actualSlTriggerPrice] = useMemo(() => {
    const tpFromPrice = parseFloat(tpTriggerPrice);
    const slFromPrice = parseFloat(slTriggerPrice);
    const tpFallback =
      Number.isFinite(tpFromPrice) ? tpFromPrice : triggerPriceFromRoi(parseFloat(tpRoi));
    const slFallback =
      Number.isFinite(slFromPrice) ? slFromPrice : triggerPriceFromRoi(parseFloat(slRoi));
    return [Math.max(0, tpFallback || 0), Math.max(0, slFallback || 0)];
  }, [tpTriggerPrice, slTriggerPrice, tpRoi, slRoi, triggerPriceFromRoi]);

  const { activeOptionMarkets, activePerpMarkets } = useContext(
    MarketInstrumentContext
  );
  const asset = useMemo(
    () => getAssetFromSymbol(position?.instrument_name || ""),
    [position?.instrument_name]
  );

  const instrument = useMemo(() => {
    if (asset && position) {
      const markets =
        position.instrument_type === InstrumentTypeResponse.Option
          ? activeOptionMarkets
          : activePerpMarkets;
      return markets?.find((m) => m.underlying_asset === asset);
    }
    return undefined;
  }, [activeOptionMarkets, activePerpMarkets, asset, position]);

  const contractPriceStep = useMemo(
    () => getContractPriceStep(instrument),
    [instrument]
  );

  const onHideModal = useCallback(() => {
    reset();
    onHide?.();
    setTpType(defaultTpslType);
    setSlType(defaultTpslType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onHide]);

  const onConfirmTPSLForPosition = useCallback(async () => {
    if (!position) {
      return;
    }

    try {
      setLoading(true);
      setErrorMessage("");

      // A price of 0 means "no leg" — the TP/SL hook reads that as a removal
      // and cancels the live trigger instead of creating one.
      await createTPSLOrders(position, {
        ...desiredAdvanced,
        takeProfitPrice: actualTpTriggerPrice,
        stopLossPrice: actualSlTriggerPrice,
      });

      mutate();
      // ...and again once the backend's open-trigger cache has certainly
      // expired. `positions[].triggers` is enriched from a 5s-cached read of the
      // executor, so the immediate refetch above can still answer the trigger we
      // just cancelled — and because the selector deliberately treats an empty
      // executor list as "no information" rather than "cancelled", that stale
      // enrichment keeps a removed TP/SL in the row until the next account poll
      // (30s). One late re-read closes the window.
      window.setTimeout(mutate, TPSL_ENRICHMENT_CACHE_MS);
      onHideModal();
      playSound("click", true);
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header: t("order_confirmed_header"),
          subheader: t("order_confirmed_subheader"),
          status: ToastStatusEnum.SUCCESS,
        },
        4000
      );
    } catch (err: any) {
      // Keep the message on screen — the modal stays open so the user can fix
      // the inputs or re-sign.
      setErrorMessage(apiError(err.message) || err.message || "");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    actualSlTriggerPrice,
    actualTpTriggerPrice,
    addToast,
    apiError,
    createTPSLOrders,
    desiredAdvanced,
    mutate,
    onHideModal,
    playSound,
    position,
    t,
  ]);

  return {
    triggerPriceFromRoi,
    actualTpTriggerPrice,
    actualSlTriggerPrice,
    asset,
    instrument,
    contractPriceStep,
    onHideModal,
    onConfirmTPSLForPosition,
  };
}
